import { FormEvent, ReactNode } from "react";
import { Link } from "react-router-dom";
import { IReferralEstimatedFee } from "../../../Services/ReferralEstimatedFees";
import { IReferralType } from "../../../Services/ReferralTypes";
import IFirm from "../../../interfaces/models/IFirm";
import { firmAudienceName } from "../../../utils/firmDisplayName";
import {
  referralFieldClass,
  ReferralLoading,
  referralPageClass,
  ReferralPanel,
  referralStaffCloseClass,
  referralStaffFormActionsClass,
  referralStaffFormClass,
  referralStaffFormLabelClass,
  referralStaffFormRowClass,
  referralStaffFormTopClass,
  referralStaffHeaderClass,
  referralStaffHintClass,
  referralStaffPartyRowClass,
  referralStaffRequiredNoteClass,
  referralStaffSubmitClass,
  referralStaffTitleClass,
} from "../referralSurface";

export type PersonOption = { id: string; name: string; lastName?: string };

export type StaffReferralFormValues = {
  relationshipType: "referral" | "collaboration";
  isMultiYear: boolean;
  referralDate: string;
  fromDate: string;
  toDate: string;
  firmByCosmosId: string;
  sendByCosmosId: string;
  firmToCosmosId: string;
  sendToCosmosId: string;
  referralTypeId: string;
  otherType: string;
  estimatedFees: string;
  detail: string;
  storyId: string;
};

export type StaffReferralFormSetters = { [Key in keyof StaffReferralFormValues]: (value: StaffReferralFormValues[Key]) => void };

type Props = {
  mode: "add" | "edit";
  basePath: string;
  referralLegacyId: string;
  error: string;
  catalogsReady: boolean;
  labels: { by: string; to: string; date: string };
  firms: IFirm[];
  types: IReferralType[];
  fees: IReferralEstimatedFee[];
  byPeople: PersonOption[];
  toPeople: PersonOption[];
  values: StaffReferralFormValues;
  set: StaffReferralFormSetters;
  onSubmit: (event: FormEvent) => void;
};

export function firmLabel(firm: IFirm, staffSurface = true): string {
  return firmAudienceName(firm, staffSurface);
}

export default function StaffReferralForm({ mode, basePath, referralLegacyId, error, catalogsReady, labels, firms, types, fees, byPeople, toPeople, values, set, onSubmit }: Props) {
  return (
    <div className={`${referralPageClass} font-[Arial,sans-serif]`}>
      <div className={referralStaffHeaderClass}>
        <h2 className={referralStaffTitleClass}>{mode === "edit" ? "Member Referral or Collaboration - Modification" : "Add a Member Referral or Collaboration"}</h2>
        <Link className={referralStaffCloseClass} to={basePath}>Close</Link>
      </div>
      {error && <p className="text-feedback-error-default">{error}</p>}
      {!catalogsReady ? <ReferralLoading /> : (
        <ReferralPanel>
          <form className={referralStaffFormClass} onSubmit={onSubmit}>
            <div className={referralStaffFormTopClass}>
              <p className={referralStaffRequiredNoteClass}>All fields in BLUE are required</p>
              <div className="flex flex-wrap items-center gap-8">
                <span>Referral Id: {referralLegacyId}</span>
                <label>
                  Success Story Id: <input className={`${referralFieldClass} input-compact !w-12`} type="text" value={values.storyId} onChange={(event) => set.storyId(event.target.value)} />
                </label>
              </div>
            </div>
            <Row label="Member Interaction" required>
              <select className={`${referralFieldClass} !w-auto`} value={values.relationshipType === "collaboration" ? "1" : "2"} onChange={(event) => set.relationshipType(event.target.value === "1" ? "collaboration" : "referral")}>
                <option value="1">Collaboration</option>
                <option value="2">Referral</option>
              </select>
            </Row>
            <Row label={labels.date} required>
              <div className="flex flex-wrap items-center gap-x-8 gap-y-2">
                <input className={`${referralFieldClass} input-compact !w-[113px]`} type="date" disabled={values.isMultiYear} value={values.referralDate} onChange={(event) => set.referralDate(event.target.value)} />
                <label>
                  <input type="checkbox" checked={values.isMultiYear} onChange={(event) => set.isMultiYear(event.target.checked)} /> Multi-Year
                </label>
                <input className={`${referralFieldClass} input-compact !w-[98px]`} type="date" aria-label={`${labels.date} From`} disabled={!values.isMultiYear} value={values.fromDate} onChange={(event) => set.fromDate(event.target.value)} />
                <input className={`${referralFieldClass} input-compact !w-[98px]`} type="date" aria-label={`${labels.date} To`} disabled={!values.isMultiYear} value={values.toDate} onChange={(event) => set.toDate(event.target.value)} />
              </div>
            </Row>
            <Party title={labels.by} firms={firms} people={byPeople} firmId={values.firmByCosmosId} personId={values.sendByCosmosId} onFirm={set.firmByCosmosId} onPerson={set.sendByCosmosId} />
            <Party title={labels.to} firms={firms} people={toPeople} firmId={values.firmToCosmosId} personId={values.sendToCosmosId} onFirm={set.firmToCosmosId} onPerson={set.sendToCosmosId} />
            <Row label="Type" required>
              <div className="flex flex-wrap items-center gap-4">
                <select className={`${referralFieldClass} !w-auto`} value={values.referralTypeId} onChange={(event) => set.referralTypeId(event.target.value)}>
                  <option value="">Please select a transaction</option>
                  {types.map((type) => (
                    <option key={type.id} value={type.typeId}>{type.description}</option>
                  ))}
                  <option value="0">Add Type</option>
                </select>
                {values.referralTypeId === "0" && (
                  <input className={`${referralFieldClass} input-compact !w-[245px]`} type="text" aria-label="Other Type" value={values.otherType} onChange={(event) => set.otherType(event.target.value)} />
                )}
              </div>
            </Row>
            <Row label="Detail">
              <div>
                <span className={referralStaffHintClass}>Maximum 6,000 Characters (Including Spaces)</span>
                <textarea className={`${referralFieldClass} input-compact !w-[600px] max-w-full h-[365px] !p-[2px] !leading-[18px] !text-gray-700`} maxLength={6000} value={values.detail} onChange={(event) => set.detail(event.target.value)} />
              </div>
            </Row>
            <Row label="Estimated Fees" required>
              <select className={`${referralFieldClass} !w-auto`} value={values.estimatedFees} onChange={(event) => set.estimatedFees(event.target.value)}>
                <option value="">Please select a transaction</option>
                {fees.map((fee) => (
                  <option key={fee.rangeId} value={fee.rangeId}>{fee.description}</option>
                ))}
              </select>
            </Row>
            <div className={referralStaffFormActionsClass}>
              <button className={referralStaffSubmitClass} type="submit">{mode === "edit" ? "Save" : "Submit"}</button>
            </div>
          </form>
        </ReferralPanel>
      )}
    </div>
  );
}

function Row({ label, required, children }: { label: string; required?: boolean; children: ReactNode }) {
  return (
    <div className={referralStaffFormRowClass}>
      <span className={referralStaffFormLabelClass(Boolean(required))}>{label}:</span>
      {children}
    </div>
  );
}

function Party({ title, firms, people, firmId, personId, onFirm, onPerson }: { title: string; firms: IFirm[]; people: PersonOption[]; firmId: string; personId: string; onFirm: (value: string) => void; onPerson: (value: string) => void }) {
  return (
    <div className="flex flex-col gap-1">
      <span className={referralStaffFormLabelClass(true)}>{title}:</span>
      <div className={referralStaffPartyRowClass}>
        <span className={referralStaffFormLabelClass(true)}>Firm:</span>
        <select className={`${referralFieldClass} !w-[245px]`} value={firmId} onChange={(event) => onFirm(event.target.value)}>
          <option value="">-- Please Select --</option>
          {firms.map((firm) => (
            <option key={firm.id} value={firm.id}>{firmLabel(firm)}</option>
          ))}
        </select>
      </div>
      <div className={referralStaffPartyRowClass}>
        <span className={referralStaffFormLabelClass(false)}>Contact:</span>
        <select className={`${referralFieldClass} !w-auto`} value={personId} onChange={(event) => onPerson(event.target.value)}>
          <option value="">-- Please Select --</option>
          {people.map((person) => (
            <option key={person.id} value={person.id}>{[person.name, person.lastName].filter(Boolean).join(" ")}</option>
          ))}
        </select>
      </div>
    </div>
  );
}
